export type DayGroup<P> = { dayKey: string; date: Date; photos: P[] }
export type GridRow<P> =
  | { kind: 'header'; dayKey: string; date: Date; count: number }
  | { kind: 'photos'; dayKey: string; photos: P[] }

const GAP = 8
const TILE_PX = { S: 120, M: 180, L: 260 }

const pad = (n: number) => (n < 10 ? `0${n}` : `${n}`)

/** Groups by LOCAL calendar day, newest day first; photo order within a day is kept. */
export function groupByDay<P extends { capturedAt: string }>(photos: P[]): DayGroup<P>[] {
  const byKey: Record<string, DayGroup<P>> = {}
  const groups: DayGroup<P>[] = []
  photos.forEach((photo) => {
    const d = new Date(photo.capturedAt)
    const dayKey = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
    if (!byKey[dayKey]) {
      byKey[dayKey] = { dayKey, date: new Date(d.getFullYear(), d.getMonth(), d.getDate()), photos: [] }
      groups.push(byKey[dayKey])
    }
    byKey[dayKey].photos.push(photo)
  })
  return groups.sort((a, b) => (a.dayKey < b.dayKey ? 1 : a.dayKey > b.dayKey ? -1 : 0))
}

/** One pseudo-group for ordered lists (albums): no day split, order kept. */
export function groupAsOne<P>(photos: P[]): DayGroup<P>[] {
  return photos.length === 0 ? [] : [{ dayKey: 'all', date: new Date(0), photos }]
}

export function buildGridRows<P>(groups: DayGroup<P>[], columns: number): GridRow<P>[] {
  const cols = Math.max(1, columns)
  const rows: GridRow<P>[] = []
  groups.forEach((g) => {
    rows.push({ kind: 'header', dayKey: g.dayKey, date: g.date, count: g.photos.length })
    for (let i = 0; i < g.photos.length; i += cols) {
      rows.push({ kind: 'photos', dayKey: g.dayKey, photos: g.photos.slice(i, i + cols) })
    }
  })
  return rows
}

export const tileSize = (size: 'S' | 'M' | 'L') => TILE_PX[size]

export function columnsFor(containerWidth: number, size: 'S' | 'M' | 'L'): number {
  return Math.max(1, Math.floor((containerWidth + GAP) / (TILE_PX[size] + GAP)))
}

export function rangeSelection(orderedIds: string[], anchorId: string, targetId: string): string[] {
  const a = orderedIds.indexOf(anchorId)
  const b = orderedIds.indexOf(targetId)
  if (a < 0 || b < 0) return [targetId]
  return orderedIds.slice(Math.min(a, b), Math.max(a, b) + 1)
}

/** Ids of loaded photos still being processed, capped per poll tick. */
export function processingIds(photos: { id: string; status: string }[], limit = 20): string[] {
  return photos.filter((p) => p.status === 'PROCESSING').slice(0, limit).map((p) => p.id)
}

/** fetchMore merge: a photo re-dated by processing (EXIF) can arrive again on a later page — keep the later copy only. */
export function mergePhotoPages<P extends { id: string }>(prev: P[], next: P[]): P[] {
  const incoming: Record<string, true> = {}
  next.forEach((p) => { incoming[p.id] = true })
  return prev.filter((p) => !incoming[p.id]).concat(next)
}
